import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { getEmbeddedResource, getUrl, hasLink } from '@ngxp/rest';
import { loadApiRootSuccessAction } from '@schir-int-client/api-root';
import { doNothingAction } from '@schir-int-client/ngrx-helpers';
import { AppNotificationService, DialogService, NotificationType } from '@schir-int-client/tech';
import { loadVerfahrenSingleAction, verfahrenSingleSelector } from '@schir-int-client/verfahren-shared';
import { isEmpty } from 'lodash-es';
import { of, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
	acknowledgeSignaturErrorAction,
	cancelSignaturAction,
	closeInProgressDialogAction,
	createSignaturTaskAction,
	downloadSignaturAction,
	downloadSignaturSuccessAction,
	downloadSignaturTaskAction,
	downloadSignaturTaskSuccessAction,
	downloadSignerScriptAction,
	loadSignaturValidatorResponseAction,
	loadSignaturValidatorResponseNotFoundAction,
	loadSignaturValidatorResponseSuccessAction,
	openInProgressDialogAction,
} from './signatur.actions';
import { SignatureTaskLinkRel, SignatureValidatorResponseListLinkRel, SignaturLinkRel } from './signatur.linkrel';
import { SignatureValidatorResponseResource } from './signatur.model';
import { SignaturService } from './signatur.service';
import { SignaturRootState } from './signatur.state';
import { inProgressDialogOpenSelector, signatureTaskSelector } from './signature.selectors';
import {
	ChangeEntryListLinkRel,
	changeEntryListSelector,
	loadChangeEntriesSuccessAction,
} from '@schir-int-client/register-change';

@Injectable()
export class SignaturEffects {
	constructor(
		private actions: Actions,
		private service: SignaturService,
		private store: Store<SignaturRootState>,
		private notificationService: AppNotificationService,
		private dialogService: DialogService
	) {}

	createSignaturTask = createEffect(() =>
		this.actions.pipe(
			ofType(createSignaturTaskAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			switchMap(([{ changeEntryUris, skipCheck }, verfahren]) =>
				this.service
					.create(verfahren, changeEntryUris, skipCheck)
					.pipe(map(signaturTask => downloadSignaturTaskSuccessAction({ signaturTask })))
			)
		)
	);

	downloadSignerScript = createEffect(
		() =>
			this.actions.pipe(
				ofType(downloadSignerScriptAction),
				withLatestFrom(this.store.select(signatureTaskSelector)),
				tap(([, task]) => {
					this.service.downloadSignerScript(task);
				})
			),
		{ dispatch: false }
	);

	downloadSignatureTask = createEffect(() =>
		this.actions.pipe(
			ofType(downloadSignaturTaskAction),
			withLatestFrom(this.store.select(signatureTaskSelector)),
			switchMap(([, task]) =>
				this.service
					.download(task)
					.pipe(map(signaturTask => downloadSignaturTaskSuccessAction({ signaturTask })))
			)
		)
	);

	openProgressDialog = createEffect(() =>
		this.actions.pipe(
			ofType(downloadSignaturTaskSuccessAction),
			withLatestFrom(this.store.select(inProgressDialogOpenSelector)),
			switchMap(([{ signaturTask }, isOpen]) => {
				if (isOpen) {
					return of(doNothingAction());
				}
				return hasLink(signaturTask, SignatureTaskLinkRel.RELOAD)
					? of(openInProgressDialogAction())
					: of(doNothingAction());
			})
		)
	);

	closeProgressDialog = createEffect(() =>
		this.actions.pipe(
			ofType(downloadSignaturTaskSuccessAction),
			withLatestFrom(this.store.select(inProgressDialogOpenSelector)),
			mergeMap(([{ signaturTask }, isOpen]) => {
				if (!isOpen) {
					return of(doNothingAction());
				}

				if (hasLink(signaturTask, SignatureTaskLinkRel.RELOAD)) {
					return [doNothingAction()];
				}
				if (hasLink(signaturTask, SignatureTaskLinkRel.VERFAHREN)) {
					return [
						closeInProgressDialogAction(),
						loadVerfahrenSingleAction({
							verfahrenUri: getUrl(signaturTask, SignatureTaskLinkRel.VERFAHREN),
						}),
					];
				}
				return [closeInProgressDialogAction()];
			})
		)
	);

	cancelSignature = createEffect(() =>
		this.actions.pipe(
			ofType(cancelSignaturAction),
			withLatestFrom(this.store.select(signatureTaskSelector)),
			switchMap(([, task]) => {
				this.notificationService.handleInfo(
					'Sie haben den Signiervorgang abgebrochen. Es wurden keine Datensätze signiert.',
					null,
					'Signiervorgang abgebrochen'
				);
				return this.service
					.cancelSignature(task)
					.pipe(map(signaturTask => downloadSignaturTaskSuccessAction({ signaturTask })));
			})
		)
	);

	loadSignatureTask = createEffect(() =>
		this.actions.pipe(
			ofType(loadChangeEntriesSuccessAction),
			switchMap(({ changeEntries }) => {
				if (hasLink(changeEntries, ChangeEntryListLinkRel.PENDING_SIGNATURE_TASK)) {
					return this.service
						.loadSignatureTaskFromRegisterEntries(changeEntries)
						.pipe(map(signaturTask => downloadSignaturTaskSuccessAction({ signaturTask })));
				}
				return of(doNothingAction());
			})
		)
	);

	downloadSignature = createEffect(() =>
		this.actions.pipe(
			ofType(downloadSignaturAction),
			mergeMap(({ changeEntry }) => {
				return this.service.downloadSignature(changeEntry).pipe(
					map(signatur =>
						downloadSignaturSuccessAction({
							signatur,
							refUrl: getUrl(changeEntry, SignaturLinkRel.DOWNLOAD_SIGNATURE),
						})
					)
				);
			})
		)
	);

	loadGlobalSignatureValidationFailures = createEffect(() =>
		this.actions.pipe(
			ofType(loadApiRootSuccessAction),
			switchMap(({ apiRoot }) => {
				return this.service.loadGlobalSignatureFailures(apiRoot).pipe(
					map(
						responseListResource =>
							<SignatureValidatorResponseResource[]>(
								getEmbeddedResource(
									responseListResource,
									SignatureValidatorResponseListLinkRel.SIGNATURE_VALIDATION_RESPONSE_LIST
								)
							)
					),
					mergeMap(errors => {
						if (isEmpty(errors)) {
							return of(doNothingAction());
						}
						return this.dialogService
							.openAcknowledgeDialog(
								`Es sind ${errors.length} Fehler bei der Prüfung der Register-Signaturen aufgetreten. Bitte informieren Sie umgehend ihren Administrator.<br/>Die Integrität des Registers ist nicht länger gewährleistet.`,
								NotificationType.ERROR
							)
							.pipe(
								map(acknowledged =>
									acknowledged ? acknowledgeSignaturErrorAction() : doNothingAction()
								)
							);
					})
				);
			})
		)
	);

	loadSignatureValidatoreResponses = createEffect(() =>
		this.actions.pipe(
			ofType(loadSignaturValidatorResponseAction),
			withLatestFrom(this.store.select(changeEntryListSelector)),
			switchMap(([, entriesList]) => {
				return this.service.loadSignatureValidatorResponses(entriesList.resource).pipe(
					map(response => loadSignaturValidatorResponseSuccessAction({ response })),
					catchError((error: HttpErrorResponse) => {
						if (error.status === 404) {
							return of(loadSignaturValidatorResponseNotFoundAction());
						}
						return throwError(() => error);
					})
				);
			})
		)
	);
}
